/** The takeoff tool rail, drawn once for both apps: the web renders it through
 *  react-native-web inside the sidebar, the native app beside its drawing canvas.
 *  Order, grouping and enablement come from toolbarModel; this only draws them. */
import React from 'react';
import { Pressable, Text, View } from 'react-native';
import type { ActiveTool } from '../../types/takeoff';
import { useSharedPalette } from '../theme';
import { Icon } from '../Icon';
import { ToolGlyphView } from './ToolGlyphView';
import { isToolEnabled, isViewEntryVisible, type ToolbarContext, type ToolbarEntry, type ViewRailEntry } from './toolbarModel';
import type { ToolGlyphName } from './toolGlyphs';

export interface TakeoffRailMetrics {
  rowHeight: number;
  glyphSize: number;
  labelSize: number;
  gap: number;
}

export const RAIL_METRICS = {
  /** Desktop and Mac: the web sidebar's own row pitch. */
  regular: { rowHeight: 36, glyphSize: 22, labelSize: 14, gap: 4 },
  /** Short viewports and the tablet sidebar. */
  compact: { rowHeight: 32, glyphSize: 18, labelSize: 12, gap: 2 },
  /** Touch targets stay at 44. */
  touch: { rowHeight: 44, glyphSize: 20, labelSize: 14, gap: 2 },
} as const satisfies Record<string, TakeoffRailMetrics>;

/** A rail row that acts on the drawing rather than choosing a cursor tool: the web's Set scale and Delete. */
export interface RailAction {
  id: string;
  label: string;
  /** Material Symbols path data. */
  iconPath: string;
  /** Appended to the label, e.g. the current scale. */
  detail?: string;
  active?: boolean;
  /** Disables the row and is read as its hint. */
  disabledReason?: string;
  onPress: () => void;
}

export interface TakeoffToolRailProps {
  groups: ToolbarEntry[][];
  ctx: ToolbarContext;
  activeTool: ActiveTool | null;
  /** `target` is the pressed row's native node, so the web can anchor a panel beside it. */
  onSelect: (entry: ToolbarEntry, target: unknown) => void;
  /** Icons only; the label stays as the accessible name. */
  collapsed?: boolean;
  metrics?: TakeoffRailMetrics;
  /** A reason a tool cannot be used in this host, shown to assistive tech. Disables the tool. */
  unavailableReason?: (entry: ToolbarEntry) => string | undefined;
  /** Replaces a glyph, e.g. the web's Autodesk icon font. Return null for the shared glyph. */
  renderGlyph?: (entry: ToolbarEntry, color: string, size: number) => React.ReactNode | null;
  /** Wraps a finished row, e.g. the web's tooltip. */
  wrapItem?: (item: React.ReactElement, entry: ToolbarEntry) => React.ReactElement;
  /** Actions that are not cursor tools (Fit to view), after a final separator. */
  viewEntries?: ViewRailEntry[];
  onSelectView?: (entry: ViewRailEntry) => void;
  actions?: RailAction[];
}

export function TakeoffToolRail({
  groups, ctx, activeTool, onSelect, collapsed = false, metrics = RAIL_METRICS.regular,
  unavailableReason, renderGlyph, wrapItem, viewEntries, onSelectView, actions,
}: TakeoffToolRailProps) {
  const palette = useSharedPalette();
  const visibleViewEntries = (viewEntries ?? []).filter(entry => isViewEntryVisible(entry, ctx));
  return (
    <View accessibilityRole="toolbar" accessibilityLabel="Takeoff tools" style={{ gap: metrics.gap }}>
      {groups.map((group, groupIndex) => (
        <React.Fragment key={group[0].group}>
          {groupIndex > 0 ? (
            <View style={{ height: 1, marginVertical: 4, marginHorizontal: 8, backgroundColor: palette.border }} />
          ) : null}
          {group.map(entry => {
            const reason = unavailableReason?.(entry);
            const disabled = !isToolEnabled(entry, ctx) || reason !== undefined;
            const active = activeTool === entry.id;
            const colour = active ? palette.foreground : palette.muted;
            const row = (
              <RailRow
                key={entry.id}
                label={entry.label}
                glyph={entry.glyph}
                drawGlyph={() => renderGlyph?.(entry, colour, metrics.glyphSize) ?? null}
                active={active}
                disabled={disabled}
                reason={reason}
                collapsed={collapsed}
                colour={colour}
                activeFill={palette.track}
                metrics={metrics}
                onPress={target => onSelect(entry, target)}
              />
            );
            return wrapItem ? React.cloneElement(wrapItem(row, entry), { key: entry.id }) : row;
          })}
        </React.Fragment>
      ))}
      {visibleViewEntries.length > 0 ? (
        <>
          <View style={{ height: 1, marginVertical: 4, marginHorizontal: 8, backgroundColor: palette.border }} />
          {visibleViewEntries.map(entry => (
            <RailRow
              key={entry.id}
              label={entry.label(false)}
              glyph={entry.id}
              drawGlyph={() => null}
              active={false}
              disabled={!(entry.enabled?.(ctx) ?? true)}
              reason={undefined}
              collapsed={collapsed}
              colour={palette.muted}
              activeFill={palette.track}
              metrics={metrics}
              onPress={() => onSelectView?.(entry)}
            />
          ))}
        </>
      ) : null}
      {actions && actions.length > 0 ? (
        <>
          <View style={{ height: 1, marginVertical: 4, marginHorizontal: 8, backgroundColor: palette.border }} />
          {actions.map(action => (
            <RailRow
              key={action.id}
              label={action.detail ? `${action.label}, ${action.detail}` : action.label}
              drawGlyph={() => <Icon d={action.iconPath} size={metrics.glyphSize} color={action.active ? palette.foreground : palette.muted} />}
              active={!!action.active}
              disabled={action.disabledReason !== undefined}
              reason={action.disabledReason}
              collapsed={collapsed}
              colour={action.active ? palette.foreground : palette.muted}
              activeFill={palette.track}
              metrics={metrics}
              onPress={action.onPress}
            />
          ))}
        </>
      ) : null}
    </View>
  );
}

function RailRow({ label, glyph, drawGlyph, active, disabled, reason, collapsed, colour, activeFill, metrics, onPress }: {
  label: string;
  glyph?: ToolGlyphName;
  drawGlyph: () => React.ReactNode | null;
  active: boolean;
  disabled: boolean;
  reason: string | undefined;
  collapsed: boolean;
  colour: string;
  activeFill: string;
  metrics: TakeoffRailMetrics;
  onPress: (target: unknown) => void;
}) {
  const node = React.useRef<View>(null);
  return (
    <Pressable
      ref={node}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint={reason}
      accessibilityState={{ selected: active, disabled }}
      // The web drops accessibilityState.selected on a button; a toggle button's DOM state is aria-pressed.
      aria-pressed={active}
      disabled={disabled}
      onPress={() => onPress(node.current)}
      style={{
        minHeight: metrics.rowHeight,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: 12,
        paddingHorizontal: collapsed ? 0 : 8,
        borderRadius: 9,
        backgroundColor: active ? activeFill : 'transparent',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      <View style={{ width: 28, alignItems: 'center', justifyContent: 'center' }}>
        {drawGlyph() ?? (glyph ? <ToolGlyphView name={glyph} size={metrics.glyphSize} color={colour} /> : null)}
      </View>
      {collapsed ? null : (
        <Text numberOfLines={1} style={{ color: colour, fontSize: metrics.labelSize, fontWeight: active ? '600' : '500' }}>
          {label}
        </Text>
      )}
    </Pressable>
  );
}
